import { useOutsideClickEffect } from "@saviynt/common";
import {
  AlertBanner,
  ButtonIcon,
  Icon,
  type IconKind,
  Menu,
  MenuItem,
  Tooltip,
} from "@saviynt/design-system";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useViewport } from "../../../../../contexts/ViewportContext";
import { useDisableUser } from "../../../hooks/useDisableUser";
import { useDisableUserTOTP } from "../../../hooks/useDisableUserTOTP";
import { useEnableUser } from "../../../hooks/useEnableUser";
import { useReserUserPassword } from "../../../hooks/useResetUserPassword";
import { getKebabMenuList } from "../../../UserTableConfig";

interface IdentityKebabMenu {
  label: string;
  value: string;
  id: string;
  isDisabled?: boolean;
  suffixIcon?: string;
  prefixIcon?: string;
  tooltip?: {
    isShow: boolean;
    value: string;
  };
  classNames?: string[];
  onClick?: () => void; // Add onClick handler
}

interface ActionButtonProps {
  id: string;
  /** Target row user email (API subject). */
  userEmail: string;
  /** Current status of the user (e.g. "ENABLED", "DISABLED"). */
  userStatus: string;
}

export default function ActionButton({
  id,
  userEmail,
  userStatus,
}: ActionButtonProps) {
  const intl = useIntl();
  const { sidenavWidth, headerHeight } = useViewport();
  const [isKebabMenuOpen, setIsKebabMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const resetUserPassword = useReserUserPassword();
  const disableUser = useDisableUser();
  const enableUser = useEnableUser();
  const disableUserTOTP = useDisableUserTOTP();

  const [isUserPasswordReset, setIsUserPasswordReset] = useState(false);
  const [isDisableUser, setIsDisableUser] = useState(false);
  const [isEnableUser, setIsEnableUser] = useState(false);
  const [isDisableUserTOTP, setIsDisableUserTOTP] = useState(false);

  useOutsideClickEffect(
    menuRef as React.RefObject<HTMLElement>,
    () => setIsKebabMenuOpen(false),
    true,
  );

  const menuOptions = useMemo<IdentityKebabMenu[]>(
    () =>
      getKebabMenuList(intl, userStatus).map((list) => ({
        ...list,
        id,
        suffixIcon: "",
        isDisabled: false,
        isIconFillDisabled: true,
      })),
    [intl, id, userStatus],
  );

  /**
   * Fixed banners must track host sidenav width (expand/collapse) so they
   * align with the main column, same as the table — not full viewport width.
   */
  const alertFloatingPosition = useMemo(
    () => ({
      type: "fixed" as const,
      top: headerHeight > 0 ? `${headerHeight}px` : "4.25rem",
      left: `${sidenavWidth}px`,
      right: "0",
    }),
    [sidenavWidth, headerHeight],
  );

  const handleKebabMenuClick = useCallback(
    (selectedOption: IdentityKebabMenu) => {
      switch (selectedOption.value) {
        case "resetPassword":
          resetUserPassword.mutate(
            { email: userEmail },
            {
              onSettled: () => {
                setIsUserPasswordReset(true);
              },
            },
          );
          break;
        case "disableUser":
          disableUser.mutate(
            { email: userEmail },
            {
              onSettled: () => {
                setIsDisableUser(true);
              },
            },
          );
          break;
        case "enableUser":
          enableUser.mutate(
            { email: userEmail },
            {
              onSettled: () => {
                setIsEnableUser(true);
              },
            },
          );
          break;
        case "resetTOTP":
          disableUserTOTP.mutate(
            { email: userEmail },
            {
              onSettled: () => {
                setIsDisableUserTOTP(true);
              },
            },
          );
          break;
      }
      setIsKebabMenuOpen(false);
    },
    [userEmail],
  );

  const getMenuItem = (option: IdentityKebabMenu, index: number) => (
    <MenuItem
      key={option.value}
      option={option}
      onClick={() => !option.isDisabled && handleKebabMenuClick(option)}
      tabIndex={index}
      prefixIcon={
        option.prefixIcon ? (
          <Icon kind={option.prefixIcon as IconKind} />
        ) : undefined
      }
      suffixIcon={
        option.suffixIcon ? (
          <Icon kind={option.suffixIcon as IconKind} />
        ) : undefined
      }
      className={
        Array.isArray(option.classNames) ? option.classNames.join(" ") : ""
      }
    />
  );

  const getCustomKebabMenuItems = (options: IdentityKebabMenu[]) => (
    <div ref={menuRef}>
      {options.map((option, index) =>
        option.tooltip?.isShow ? (
          <Tooltip
            isPortalEnabled
            className={`Ispm-IdentityCard-${option.value} AgentIdentityCard-tooltip`}
            key={option.value}
            text={option.tooltip.value}
            placement="left-start"
            trigger={getMenuItem(option, index)}
          />
        ) : (
          getMenuItem(option, index)
        ),
      )}
    </div>
  );

  return (
    <>
      <Menu
        isPortalEnabled
        options={menuOptions}
        onChange={handleKebabMenuClick}
        trigger={
          <ButtonIcon
            size="medium"
            kind="ghost"
            ariaLabel=""
            data-testid="menu-trigger"
            icon={
              <Icon kind="MoreVertical" color="neutral-100" size="medium" />
            }
          />
        }
        CustomMenuItems={getCustomKebabMenuItems(menuOptions)}
        dataTestId="ispm-identities-kebabMenu"
        isOutsideClickEnabled={false}
        isMenuExpanded={isKebabMenuOpen}
        setIsExpanded={setIsKebabMenuOpen}
      />

      <AlertBanner
        colorTheme={resetUserPassword.isSuccess ? "Success" : "Critical"}
        title={
          resetUserPassword.isSuccess
            ? intl.formatMessage({
                defaultMessage: "Success!",
                description: "Success!",
                id: "lA8AMx",
              })
            : intl.formatMessage({
                defaultMessage: "Failed!",
                description: "Failed!",
                id: "1/SX1h",
              })
        }
        description={
          resetUserPassword.isSuccess
            ? intl.formatMessage({
                defaultMessage: "Reset Password successfull",
                description: "Reset Password successfull",
                id: "KYhUJ3",
              })
            : intl.formatMessage({
                defaultMessage: "Reset Password failed",
                description: "Reset Password failed",
                id: "AZWJAW",
              })
        }
        isVisible={isUserPasswordReset}
        onCancel={() => setIsUserPasswordReset(false)}
        isColonVisible={false}
        isFloating
        position={alertFloatingPosition}
        className="!z-[1020]"
      />

      <AlertBanner
        colorTheme={disableUser.isSuccess ? "Success" : "Critical"}
        title={
          disableUser.isSuccess
            ? intl.formatMessage({
                defaultMessage: "Success!",
                description: "Success!",
                id: "lA8AMx",
              })
            : intl.formatMessage({
                defaultMessage: "Failed!",
                description: "Failed!",
                id: "1/SX1h",
              })
        }
        description={
          disableUser.isSuccess
            ? intl.formatMessage({
                defaultMessage: "Disabled User",
                description: "Disabled User",
                id: "q9KmUe",
              })
            : intl.formatMessage({
                defaultMessage: "Disabled User",
                description: "Disabled User",
                id: "q9KmUe",
              })
        }
        isVisible={isDisableUser}
        onCancel={() => setIsDisableUser(false)}
        isColonVisible={false}
        isFloating
        position={alertFloatingPosition}
        className="!z-[1020]"
      />

      <AlertBanner
        colorTheme={enableUser.isSuccess ? "Success" : "Critical"}
        title={
          enableUser.isSuccess
            ? intl.formatMessage({
                defaultMessage: "Success!",
                description: "Success!",
                id: "lA8AMx",
              })
            : intl.formatMessage({
                defaultMessage: "Failed!",
                description: "Failed!",
                id: "1/SX1h",
              })
        }
        description={
          enableUser.isSuccess
            ? intl.formatMessage({
                defaultMessage: "Enabled User",
                description: "Enabled User",
                id: "NmO6fw",
              })
            : intl.formatMessage({
                defaultMessage: "Enable User failed",
                description: "Enable User failed",
                id: "3E5WWw",
              })
        }
        isVisible={isEnableUser}
        onCancel={() => setIsEnableUser(false)}
        isColonVisible={false}
        isFloating
        position={alertFloatingPosition}
        className="!z-[1020]"
      />

      <AlertBanner
        colorTheme={disableUserTOTP.isSuccess ? "Success" : "Critical"}
        title={
          disableUserTOTP.isSuccess
            ? intl.formatMessage({
                defaultMessage: "Success!",
                description: "Success!",
                id: "lA8AMx",
              })
            : intl.formatMessage({
                defaultMessage: "Failed!",
                description: "Failed!",
                id: "1/SX1h",
              })
        }
        description={
          disableUserTOTP.isSuccess
            ? intl.formatMessage({
                defaultMessage: "Disabled User TOTP",
                description: "Disabled User TOTP",
                id: "h4lki5",
              })
            : intl.formatMessage({
                defaultMessage: "Disabled User TOTP",
                description: "Disabled User TOTP",
                id: "h4lki5",
              })
        }
        isVisible={isDisableUserTOTP}
        onCancel={() => setIsDisableUserTOTP(false)}
        isColonVisible={false}
        isFloating
        position={alertFloatingPosition}
        className="!z-[1020]"
      />
    </>
  );
}
